import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import DOMPurify from "dompurify";
import { Box } from "@mui/material";

interface MarkdownViewerProps {
  markdown: string;
  mode: "light" | "dark";
}

const MarkdownViewer: React.FC<MarkdownViewerProps> = ({ markdown, mode }) => {
  const textColor = mode === "light" ? "#000000" : "#FFFFFF";

  const sanitized = useMemo(
    () =>
      DOMPurify.sanitize(markdown ?? "", {
        ALLOWED_TAGS: [
          "p", "br", "b", "i", "strong", "em", "code", "pre",
          "ul", "ol", "li", "a", "blockquote",
          "h1", "h2", "h3", "h4", "h5", "h6",
        ],
        ALLOWED_ATTR: ["href", "title", "class"],
      }),
    [markdown],
  );

  return (
    <Box
      sx={{
        color: textColor,
        "& a": { color: "inherit", textDecoration: "underline" },
        "& pre": {
          backgroundColor: mode === "light" ? "#F5F6F6" : "#232529",
          padding: "8px",
          borderRadius: "4px",
          overflowX: "auto",
        },
        "& code": {
          backgroundColor: mode === "light" ? "#F5F6F6" : "#232529",
          padding: "2px 4px",
          borderRadius: "3px",
          fontSize: "0.9em",
        },
        "& blockquote": {
          borderLeft: "3px solid",
          borderColor: mode === "light" ? "#CDD0D4" : "#565C64",
          paddingLeft: "12px",
          marginLeft: 0,
        },
      }}
    >
      <ReactMarkdown
        components={{
          a: ({ node, children, ...props }) => (
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            <a {...props} target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
        }}
      >
        {sanitized}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownViewer;
